import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCoins } from "../lib/marketApi";
import type { Coin } from "../lib/marketApi";

interface Quote {
  symbol: string;
  price: string;
  price24hPcnt: string;
  highPrice24h: string;
  lowPrice24h: string;
  volume24h: string;
  timestamp: string;
}

function formatPrice(price: string): string {
  const num = parseFloat(price);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatVolume(volume: string): string {
  const num = parseFloat(volume);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
  return num.toFixed(2);
}

function formatPercent(pct: string): string {
  const num = parseFloat(pct);
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

export default function MarketPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const [status, setStatus] = useState<
    "connecting" | "open" | "closed" | "error"
  >("connecting");
  const [coins, setCoins] = useState<Coin[]>([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("layer1");
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const watchdogTimerRef = useRef<number | null>(null);
  const lastEventAtRef = useRef<number>(Date.now());
  const reconnectAttemptsRef = useRef<number>(0);

  const apiUrl = import.meta.env.VITE_API_URL || "https://api.wedopay.dev";

  const IDLE_TIMEOUT_MS = 15000;
  const WATCHDOG_INTERVAL_MS = 5000;
  const MAX_BACKOFF_MS = 30000;

  // Fetch coins from API
  useEffect(() => {
    const fetchCoins = async () => {
      setCoinsLoading(true);
      try {
        const data = await getCoins();
        setCoins(Array.isArray(data) ? data : []);
        const uniqueCategories = [
          ...new Set(data.map((c) => c.category)),
        ];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Erro ao buscar coins:", error);
      } finally {
        setCoinsLoading(false);
      }
    };
    fetchCoins();
  }, []);

  // Derive symbols from coins (first USDT pair of each coin)
  const filteredCoins = selectedCategory === "all"
    ? coins
    : coins.filter((c) => c.category === selectedCategory);

  const symbolToCoin = new Map<string, Coin>();
  const symbols: string[] = [];
  for (const coin of filteredCoins) {
    const usdtPair = coin.pairs.find((p) => p.quoteCoin === "USDT");
    if (usdtPair) {
      symbols.push(usdtPair.symbol);
      symbolToCoin.set(usdtPair.symbol, coin);
    }
  }

  const connectRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (watchdogTimerRef.current !== null) {
      window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current !== null) return;
    const attempt = reconnectAttemptsRef.current;
    const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
    reconnectAttemptsRef.current = attempt + 1;
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      connectRef.current();
    }, delay);
  }, []);

  const connect = useCallback(() => {
    if (symbols.length === 0) return;

    clearTimers();
    eventSourceRef.current?.close();
    setStatus("connecting");
    lastEventAtRef.current = Date.now();

    const url = `${apiUrl}/market/quote/stream?symbols=${symbols.join(",")}`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    const markActivity = () => {
      lastEventAtRef.current = Date.now();
    };

    es.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setStatus("open");
      markActivity();
    };
    es.onerror = () => {
      setStatus("error");
      es.close();
      eventSourceRef.current = null;
      scheduleReconnect();
    };

    es.addEventListener("stream_start", (e: MessageEvent) => {
      markActivity();
      try {
        const data: { symbols: string[] } = JSON.parse(e.data);
        setQuotes((prev) => {
          const next: Record<string, Quote> = {};
          for (const s of data.symbols) {
            if (prev[s]) next[s] = prev[s];
          }
          return next;
        });
      } catch {
        // ignore parse errors
      }
    });

    es.addEventListener("stream_state", (e: MessageEvent) => {
      markActivity();
      try {
        const data: Quote = JSON.parse(e.data);
        setQuotes((prev) => ({ ...prev, [data.symbol]: data }));
      } catch {
        // ignore parse errors
      }
    });

    watchdogTimerRef.current = window.setInterval(() => {
      if (Date.now() - lastEventAtRef.current > IDLE_TIMEOUT_MS) {
        setStatus("error");
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
        if (watchdogTimerRef.current !== null) {
          window.clearInterval(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
        scheduleReconnect();
      }
    }, WATCHDOG_INTERVAL_MS);
  }, [apiUrl, symbols.join(","), clearTimers, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (symbols.length > 0) {
      connect();
    }
    return () => {
      clearTimers();
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connect, clearTimers]);

  const handleReconnect = () => {
    reconnectAttemptsRef.current = 0;
    clearTimers();
    connect();
  };

  return (
    <div className="page-centered">
      <div className="quotes-card">
        <div className="quotes-header">
          <button
            type="button"
            className="quotes-back"
            onClick={() => navigate("/profile")}
          >
            ← Voltar
          </button>
          <h1>Market</h1>
          <div className="quotes-status">
            <span
              className={`status-dot ${
                status === "open" ? "status-dot--live" : ""
              }`}
            />
            <span className="status-label">
              {status === "open"
                ? "Live"
                : status === "connecting"
                ? "Conectando..."
                : "Desconectado"}
            </span>
          </div>
        </div>

        {categories.length > 0 && (
          <div className="quotes-categories">
            <button
              type="button"
              className={`category-tab ${
                selectedCategory === "all" ? "category-tab--active" : ""
              }`}
              onClick={() => setSelectedCategory("all")}
            >
              Todas
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`category-tab ${
                  selectedCategory === cat ? "category-tab--active" : ""
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {status === "error" && (
          <div className="quotes-error">
            <p>Conexao perdida com o servidor.</p>
            <button
              type="button"
              className="primary-button"
              onClick={handleReconnect}
              style={{ maxWidth: "200px" }}
            >
              Reconectar
            </button>
          </div>
        )}

        {coinsLoading
          ? (
            <div className="quote-loading" style={{ padding: "2rem" }}>
              Carregando moedas...
            </div>
          )
          : (
            <div className="quotes-grid">
              {symbols.map((symbol) => {
                const q = quotes[symbol];
                const coin = symbolToCoin.get(symbol);
                const isPositive = q ? parseFloat(q.price24hPcnt) >= 0 : true;

                return (
                  <div
                    key={symbol}
                    className="quote-tile"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      coin &&
                      navigate(`/wallet/portfolio/${coin.code}`, {
                        state: {
                          code: coin.code,
                          name: coin.name,
                          icon: coin.icon,
                        },
                      })}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && coin) {
                        e.preventDefault();
                        navigate(`/wallet/portfolio/${coin.code}`, {
                          state: {
                            code: coin.code,
                            name: coin.name,
                            icon: coin.icon,
                          },
                        });
                      }
                    }}
                  >
                    <div className="quote-tile-header">
                      <div className="quote-symbol-badge">
                        {coin?.icon
                          ? (
                            <img
                              src={coin.icon}
                              alt={coin.name}
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                              }}
                            />
                          )
                          : (
                            symbol.replace("USDT", "")
                          )}
                      </div>
                      <div>
                        <div className="quote-symbol">{symbol}</div>
                        <div className="quote-name">
                          {coin?.name ?? symbol}
                        </div>
                      </div>
                    </div>

                    {q
                      ? (
                        <>
                          <div className="quote-price">
                            ${formatPrice(q.price)}
                          </div>
                          <div
                            className={`quote-change ${
                              isPositive
                                ? "quote-change--up"
                                : "quote-change--down"
                            }`}
                          >
                            {formatPercent(q.price24hPcnt)}
                          </div>
                          <div className="quote-details">
                            <div className="quote-detail">
                              <span className="quote-detail-label">
                                High 24h
                              </span>
                              <span className="quote-detail-value">
                                ${formatPrice(q.highPrice24h)}
                              </span>
                            </div>
                            <div className="quote-detail">
                              <span className="quote-detail-label">
                                Low 24h
                              </span>
                              <span className="quote-detail-value">
                                ${formatPrice(q.lowPrice24h)}
                              </span>
                            </div>
                            <div className="quote-detail">
                              <span className="quote-detail-label">
                                Vol 24h
                              </span>
                              <span className="quote-detail-value">
                                {formatVolume(q.volume24h)}
                              </span>
                            </div>
                          </div>
                        </>
                      )
                      : (
                        <div className="quote-loading">
                          Aguardando dados...
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
